import { Button, Card, Muted, Screen, Stack, color, radius, space, status } from '@biopeak/ui';
import { useState } from 'react';
import { useUnreadFeedDays } from '../lib/communication.ts';
import { formatElapsed } from '../lib/format.ts';
import {
  useLogWhatsApp,
  useOverrideStatus,
  usePatientHeader,
  whatsappUrl,
  type TrafficLight,
} from '../lib/patient.ts';
import { PatientAlertRules } from './PatientAlertRules.tsx';
import { PatientCommunication } from './PatientCommunication.tsx';
import { PatientDocuments } from './PatientDocuments.tsx';
import { PatientNotes } from './PatientNotes.tsx';
import { PatientOverview } from './PatientOverview.tsx';
import { PatientReport } from './PatientReport.tsx';
import { SerbioPanel } from './SerbioPanel.tsx';

const STATUS_LABEL: Record<TrafficLight, string> = {
  red: 'Vermelho',
  yellow: 'Amarelo',
  green: 'Verde',
};

/**
 * Perfil do paciente (§4.2) — shell.
 *
 * O que fica FORA das abas é o que o médico precisa ver independentemente do
 * que está fazendo: quem é o paciente, em que estado está, como falar com ele e
 * como corrigir o estado. O que entra em aba é o trabalho em si.
 *
 * As abas existem por causa do §8.2 princípio 3 ("uma ideia por seção/tela").
 * Resumo clínico, conversa e configuração de limiares são três assuntos com
 * ritmos diferentes — empilhados na mesma rolagem, competiriam entre si e a
 * tela viraria painel de instrumentos, exatamente o que o §8.4 pede para evitar.
 */

type Tab = 'overview' | 'serbio' | 'report' | 'communication' | 'rules' | 'documents';

// SERBIO logo depois de Exames de propósito: ele é leitura DO exame, e o
// médico chega nele vindo de lá — confirmou os valores na revisão, quer ver
// o que os cruzamentos dizem.
const TABS: { id: Tab; label: string }[] = [
  { id: 'overview', label: 'Visão geral' },
  { id: 'documents', label: 'Exames' },
  { id: 'serbio', label: 'SERBIO' },
  { id: 'report', label: 'Relatório' },
  { id: 'communication', label: 'Comunicação' },
  { id: 'rules', label: 'Alertas' },
];

export function PatientProfile({ patientId, onBack }: { patientId: string; onBack: () => void }) {
  const header = usePatientHeader(patientId);
  const patient = header.data;

  const [tab, setTab] = useState<Tab>('overview');
  const unreadDays = useUnreadFeedDays(patientId);

  if (header.isLoading) {
    return (
      <Screen style={{ maxWidth: '1440px' }}>
        <Muted>Carregando…</Muted>
      </Screen>
    );
  }

  if (!patient) {
    return (
      <Screen style={{ maxWidth: '1440px' }}>
        <Button variant="quiet" onClick={onBack}>
          ‹ Voltar para a triagem
        </Button>
        <Card>
          <Muted>Paciente não encontrado entre os seus.</Muted>
        </Card>
      </Screen>
    );
  }

  const tone = (patient.status ?? 'green') as TrafficLight;

  return (
    <Screen style={{ maxWidth: '1440px' }}>
      <Button variant="quiet" onClick={onBack} style={{ alignSelf: 'flex-start' }}>
        ‹ Voltar para a triagem
      </Button>

      {/* ---------------------------------------------------- cabeçalho */}
      <Stack gap={space.xs}>
        <h1 style={{ margin: 0 }}>{patient.full_name}</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexWrap: 'wrap' }}>
          <StatusDot tone={tone} />
          <span style={{ fontWeight: 600 }}>{STATUS_LABEL[tone]}</span>
          {patient.is_manual_override && (
            <span style={{ fontSize: '12px', color: color.textSecondary }}>· manual</span>
          )}
          <Muted style={{ fontSize: '13px' }}>
            {formatElapsed(patient.entered_state_at)} · sincronizou{' '}
            {formatElapsed(patient.last_sync_at)}
          </Muted>
        </div>
        {patient.reason && <Muted style={{ fontSize: '14px' }}>{patient.reason}</Muted>}
      </Stack>

      {/* Sinal discreto do §5.3, e discreto é requisito, não estilo: o §10 é
          explícito que comunicado não lido NÃO vira alerta vermelho automático.
          Por isso sem cor de status — é informação, não gravidade clínica.
          Fica fora das abas porque é sinal do paciente, não de uma delas. */}
      {unreadDays !== null && (
        <Muted style={{ fontSize: '13px' }}>
          Comunicado não lido há {unreadDays} {unreadDays === 1 ? 'dia' : 'dias'}.
        </Muted>
      )}

      <ContactCard patientId={patientId} phone={patient.phone_e164} name={patient.full_name} />

      <OverrideCard patientId={patientId} current={tone} />

      {/* --------------------------------------------------------- abas */}
      {/* Rolável na horizontal no celular (`.doctor-tabbar`, global.css) —
          4 pills com padding confortável não cabem numa linha só a 375px, e
          deixar quebrar em 2 linhas ficava desalinhado. Sem quebra: uma
          fileira só, com scroll por swipe, como abas de app nativo. */}
      <div className="doctor-tabbar">
        {TABS.map((t) => (
          <TabPill
            key={t.id}
            label={t.label}
            active={tab === t.id}
            onClick={() => setTab(t.id)}
            badge={
              t.id === 'communication'
                ? patient.unread_patient_messages ?? 0
                : t.id === 'documents'
                  ? patient.new_documents ?? 0
                  : 0
            }
          />
        ))}
      </div>

      {tab === 'overview' && (
        <>
          <PatientOverview patientId={patientId} />
          {/* A outra metade da contestabilidade: o que o paciente explicou
              sobre os próprios números. Fica na visão geral porque é contexto
              do dado, não assunto separado. */}
          <PatientNotes patientId={patientId} />
        </>
      )}
      {tab === 'documents' && <PatientDocuments patientId={patientId} />}
      {tab === 'serbio' && <SerbioPanel patientId={patientId} />}
      {tab === 'report' && <PatientReport patientId={patientId} />}
      {tab === 'communication' && <PatientCommunication patientId={patientId} />}
      {tab === 'rules' && <PatientAlertRules patientId={patientId} />}
    </Screen>
  );
}

// ------------------------------------------------------------ WhatsApp

function ContactCard({
  patientId,
  phone,
  name,
}: {
  patientId: string;
  phone: string | null;
  name: string | null;
}) {
  const logOpen = useLogWhatsApp(patientId);

  if (!phone) {
    return (
      <Card compact>
        <Muted>Sem telefone cadastrado para este paciente.</Muted>
      </Card>
    );
  }

  const firstName = (name ?? '').split(' ')[0];
  const url = whatsappUrl(phone, `Olá, ${firstName}. Aqui é do acompanhamento médico.`);

  return (
    <Card compact>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: space.md,
          flexWrap: 'wrap',
        }}
      >
        <Stack gap={space.xs} style={{ minWidth: 0 }}>
          <span style={{ fontWeight: 600 }}>Contato direto</span>
          {/* §4.2 exige que a limitação apareça na UI, não só na documentação:
              o médico precisa saber, no momento em que clica, que o que ele
              disser ali não vai estar no prontuário depois. */}
          <Muted style={{ fontSize: '13px' }}>
            A conversa acontece no seu WhatsApp pessoal e <strong>não fica registrada aqui</strong>
            . Só o fato de você ter aberto o contato é auditado.
          </Muted>
        </Stack>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => logOpen.mutate()}
          style={{
            background: color.accent,
            color: color.white,
            borderRadius: radius.pill,
            padding: '10px 20px',
            fontSize: '15px',
            fontWeight: 500,
            textDecoration: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          Abrir WhatsApp
        </a>
      </div>
    </Card>
  );
}

// ------------------------------------------------------------- Override

function OverrideCard({ patientId, current }: { patientId: string; current: TrafficLight }) {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState<TrafficLight>(current);
  const [reason, setReason] = useState('');
  const override = useOverrideStatus(patientId);

  const tooShort = reason.trim().length < 10;

  if (!open) {
    return (
      <Card compact>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: space.md,
          }}
        >
          <Muted style={{ fontSize: '14px' }}>Discorda do estado atual?</Muted>
          <Button
            variant="secondary"
            onClick={() => {
              setTarget(current);
              setReason('');
              override.reset();
              setOpen(true);
            }}
            style={{ fontSize: '15px', padding: '10px 20px' }}
          >
            Alterar manualmente
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <Stack gap={space.md}>
        <span style={{ fontWeight: 600 }}>Alterar o estado manualmente</span>

        <div style={{ display: 'flex', gap: space.sm }}>
          {(['red', 'yellow', 'green'] as TrafficLight[]).map((s) => (
            <button
              key={s}
              onClick={() => setTarget(s)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: space.xs,
                border: `1px solid ${target === s ? status[s].fg : color.border}`,
                background: target === s ? status[s].bg : color.white,
                borderRadius: radius.pill,
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: target === s ? 600 : 400,
                cursor: 'pointer',
              }}
            >
              <StatusDot tone={s} />
              {STATUS_LABEL[s]}
            </button>
          ))}
        </div>

        <Stack gap={space.xs}>
          <label htmlFor="override-reason" style={{ fontSize: '14px', fontWeight: 500 }}>
            Justificativa (obrigatória)
          </label>
          <textarea
            id="override-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="O que você observou que justifica esse estado?"
            style={{
              border: `1px solid ${color.border}`,
              borderRadius: radius.input,
              padding: '10px 12px',
              fontSize: '15px',
              fontFamily: 'inherit',
              resize: 'vertical',
            }}
          />
          {/* A obrigatoriedade também é CHECK constraint no banco. Isto aqui é
              cortesia com o médico, não a garantia — formulário é contornável. */}
          <Muted style={{ fontSize: '12px' }}>
            Vai para a auditoria com seu nome e a data. Mínimo de 10 caracteres.
          </Muted>
        </Stack>

        {override.isError && (
          <Muted style={{ color: status.red.fg, fontSize: '13px' }}>
            {(override.error as Error).message}
          </Muted>
        )}

        <div style={{ display: 'flex', gap: space.sm, justifyContent: 'flex-end' }}>
          <Button
            variant="secondary"
            onClick={() => setOpen(false)}
            style={{ fontSize: '15px', padding: '10px 20px' }}
          >
            Cancelar
          </Button>
          <Button
            disabled={tooShort || override.isPending}
            onClick={() =>
              override.mutate({ status: target, reason }, { onSuccess: () => setOpen(false) })
            }
            style={{ fontSize: '15px', padding: '10px 20px' }}
          >
            {override.isPending ? 'Salvando…' : 'Confirmar alteração'}
          </Button>
        </div>
      </Stack>
    </Card>
  );
}

function StatusDot({ tone }: { tone: TrafficLight }) {
  return (
    <span
      aria-hidden
      style={{
        display: 'inline-block',
        width: 10,
        height: 10,
        borderRadius: '50%',
        background: status[tone].fg,
        flexShrink: 0,
      }}
    />
  );
}

/**
 * Aba do perfil. Mesma receita visual das pills de período e de filtro da
 * triagem, e `<button>` de verdade pela mesma razão que a linha da triagem é:
 * o painel precisa ser percorrível por teclado.
 */
function TabPill({
  label,
  active,
  onClick,
  badge,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
  badge?: number;
}) {
  return (
    <button
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: space.xs,
        border: `1px solid ${active ? color.accent : color.border}`,
        background: active ? `${color.accent}12` : color.white,
        color: active ? color.accent : color.text,
        borderRadius: radius.pill,
        padding: '8px 18px',
        fontSize: '15px',
        fontWeight: active ? 600 : 400,
        cursor: 'pointer',
      }}
    >
      {label}
      {!!badge && (
        <span
          aria-label={`${badge} pendente${badge === 1 ? '' : 's'}`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '18px',
            height: '18px',
            padding: '0 5px',
            borderRadius: radius.pill,
            background: '#C7332B',
            color: color.white,
            fontSize: '11px',
            fontWeight: 700,
            lineHeight: 1,
          }}
        >
          {badge > 99 ? '99+' : badge}
        </span>
      )}
    </button>
  );
}
